import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  HostListener
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TutorialService, TutorialStep } from '../../services/tutorial.service';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-tutorial',
  templateUrl: './tutorial.component.html',
  styleUrls: ['./tutorial.component.scss']
})
export class TutorialComponent implements OnInit, OnDestroy {
  showDialog = false;
  isActive = false;
  currentStepIndex = 0;
  totalSteps = 0;
  visibleStepNumber = 1;
  visibleStepTotal = 0;

  highlightRect: { top: number; left: number; width: number; height: number } | null = null;
  tooltipStyle: { [key: string]: string } = {};
  currentStep: TutorialStep | null = null;

  private subs: Subscription[] = [];
  private positionTimer: any = null;
  private scrollListener: ((e: Event) => void) | null = null;
  private blockScroll: ((e: Event) => void) | null = null;
  private blockScrollKeys: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private tutorialService: TutorialService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.tutorialService.showDialog$.subscribe(show => {
        this.showDialog = show;
        this.cdr.detectChanges();
      })
    );

    this.subs.push(
      this.tutorialService.active$.subscribe(active => {
        this.isActive = active;
        this.totalSteps = this.tutorialService.steps.length;
        if (active) {
          this.enableScrollLock();
          this.skipMissingSteps(1);
          this.updateStep();
        } else {
          this.disableScrollLock();
          this.clearPositionTimer();
        }
        this.cdr.detectChanges();
      })
    );

    this.subs.push(
      this.tutorialService.currentStep$.subscribe(index => {
        this.currentStepIndex = index;
        if (this.isActive) {
          this.updateStep();
        }
        this.cdr.detectChanges();
      })
    );
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isActive) {
      this.updateStep();
    }
  }

  startTutorial(): void {
    const role = this.getRole();
    this.tutorialService.startTutorial(role);
  }

  dismissDialog(): void {
    this.tutorialService.dismissDialog();
  }

  nextStep(): void {
    this.tutorialService.nextStep();
    this.skipMissingSteps(1);
  }

  prevStep(): void {
    this.tutorialService.prevStep();
    this.skipMissingSteps(-1);
  }

  skip(): void {
    this.tutorialService.skipTutorial();
  }

  private skipMissingSteps(direction: number): void {
    const steps = this.tutorialService.steps;
    let idx = this.tutorialService.currentStep$.value;
    let attempts = 0;
    while (idx >= 0 && idx < steps.length && attempts < steps.length) {
      const step = steps[idx];
      const el = document.querySelector(step.selector) as HTMLElement;
      if (el && el.getBoundingClientRect().width > 2) break;
      // If the step is mandatory, don't skip it even if the element is missing
      if (step.mandatory) break;

      idx += direction;
      attempts++;
    }
    if (idx >= steps.length) {
      this.tutorialService.skipTutorial();
      return;
    }
    if (idx < 0) idx = 0;
    if (idx !== this.tutorialService.currentStep$.value) {
      this.tutorialService.currentStep$.next(idx);
    }
    this.updateVisibleStepCounters();
  }

  private updateVisibleStepCounters(): void {
    const steps = this.tutorialService.steps;
    const visibleIndices: number[] = [];
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      const el = document.querySelector(step.selector) as HTMLElement;
      // If the current step or any step is missing, check if we should count it.
      // For now, we count a step if:
      // 1. It is mandatory
      // 2. Its element is present and visible
      // 3. It belongs to the same "group" (module) and we want to show total progress
      if (step.mandatory || (el && el.getBoundingClientRect().width > 2)) {
        visibleIndices.push(i);
      }
    }

    this.visibleStepTotal = visibleIndices.length;
    const currentIdx = this.tutorialService.currentStep$.value;
    const pos = visibleIndices.indexOf(currentIdx);
    this.visibleStepNumber = pos >= 0 ? pos + 1 : 1;
  }

  private updateStep(): void {
    const steps = this.tutorialService.steps;
    if (steps.length === 0) return;
    this.clearPositionTimer();
    this.updateVisibleStepCounters();

    this.currentStep = steps[this.currentStepIndex];
    const el = document.querySelector(this.currentStep.selector) as HTMLElement;

    if (el) {
      const rect = el.getBoundingClientRect();

      // If element has no dimensions, try its parent or next sibling
      let targetEl = el;
      if (rect.width < 2 || rect.height < 2) {
        const parent = el.parentElement;
        if (parent && parent.getBoundingClientRect().width > 2) {
          targetEl = parent;
        } else {
          // Element is invisible/empty - show tooltip centered
          this.highlightRect = null;
          this.tooltipStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
          return;
        }
      }

      const targetRect = targetEl.getBoundingClientRect();
      const isVisible = targetRect.top >= 0 && targetRect.bottom <= window.innerHeight;
      if (!isVisible) {
        // Temporarily allow programmatic scroll while we scroll the element into view.
        this.disableScrollLock();
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Show an initial highlight immediately so the user sees something during the scroll animation.
        this.positionHighlight(targetEl);
        this.positionTimer = setTimeout(() => {
          this.positionTimer = null;
          this.positionHighlight(targetEl);
          if (this.isActive) {
            this.enableScrollLock();
          }
        }, 400);
        return;
      }
      this.positionHighlight(targetEl);
    } else {
      this.highlightRect = null;
      this.tooltipStyle = {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }
  }

  private clearPositionTimer(): void {
    if (this.positionTimer) {
      clearTimeout(this.positionTimer);
      this.positionTimer = null;
    }
  }

  private enableScrollLock(): void {
    if (this.blockScroll) return;
    this.blockScroll = (e: Event) => {
      if (this.isActive) {
        e.preventDefault();
      }
    };
    this.blockScrollKeys = (e: KeyboardEvent) => {
      if (!this.isActive) return;
      const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar'];
      if (scrollKeys.includes(e.key)) {
        e.preventDefault();
      }
    };
    window.addEventListener('wheel', this.blockScroll, { passive: false, capture: true });
    window.addEventListener('touchmove', this.blockScroll, { passive: false, capture: true });
    window.addEventListener('keydown', this.blockScrollKeys, { capture: true });

    // Keep highlight in sync if anything still manages to scroll (e.g., programmatic scrollIntoView animations).
    this.scrollListener = () => {
      if (this.isActive && this.currentStep) {
        const el = document.querySelector(this.currentStep.selector) as HTMLElement;
        if (el && el.getBoundingClientRect().width > 2) {
          this.positionHighlight(el);
        }
      }
    };
    window.addEventListener('scroll', this.scrollListener, { capture: true, passive: true });
  }

  private disableScrollLock(): void {
    if (this.blockScroll) {
      window.removeEventListener('wheel', this.blockScroll, { capture: true } as any);
      window.removeEventListener('touchmove', this.blockScroll, { capture: true } as any);
      this.blockScroll = null;
    }
    if (this.blockScrollKeys) {
      window.removeEventListener('keydown', this.blockScrollKeys, { capture: true } as any);
      this.blockScrollKeys = null;
    }
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, { capture: true } as any);
      this.scrollListener = null;
    }
  }

  private positionHighlight(el: HTMLElement): void {
    const rect = el.getBoundingClientRect();
    const padding = 8;

    // All coordinates are viewport-relative (for position: fixed)
    this.highlightRect = {
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2
    };

    this.calculateTooltipPosition(rect, this.currentStep.position);
    this.cdr.detectChanges();
  }

  private calculateTooltipPosition(
    rect: DOMRect,
    position: 'top' | 'bottom' | 'left' | 'right'
  ): void {
    const tooltipWidth = 320;
    const tooltipGap = 16;
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Measure actual tooltip height after render, fallback to estimate
    const tooltipEl = document.querySelector('.tutorial-tooltip') as HTMLElement;
    const tooltipHeight = tooltipEl ? tooltipEl.offsetHeight : 200;

    const elTop = Math.max(rect.top, 0);
    const elBottom = Math.min(rect.bottom, vh);
    const elCenterY = elTop + (elBottom - elTop) / 2;

    let top: number;
    let left: number;

    // Try preferred position, then find best fit
    const spaceTop = rect.top;
    const spaceBottom = vh - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = vw - rect.right;

    const fits = {
      bottom: spaceBottom >= tooltipHeight + tooltipGap,
      top: spaceTop >= tooltipHeight + tooltipGap,
      right: spaceRight >= tooltipWidth + tooltipGap,
      left: spaceLeft >= tooltipWidth + tooltipGap
    };

    // Pick best position: preferred if fits, otherwise largest space
    let bestPosition = position;
    if (!fits[position]) {
      if (fits.bottom) bestPosition = 'bottom';
      else if (fits.right) bestPosition = 'right';
      else if (fits.left) bestPosition = 'left';
      else if (fits.top) bestPosition = 'top';
      else bestPosition = spaceBottom > spaceTop ? 'bottom' : 'top';
    }

    switch (bestPosition) {
      case 'right':
        top = elCenterY - tooltipHeight / 2;
        left = rect.right + tooltipGap;
        break;
      case 'left':
        top = elCenterY - tooltipHeight / 2;
        left = rect.left - tooltipWidth - tooltipGap;
        break;
      case 'bottom':
        top = rect.bottom + tooltipGap;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'top':
        top = rect.top - tooltipHeight - tooltipGap;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
    }

    if (left < margin) { left = margin; }
    if (left + tooltipWidth > vw - margin) { left = vw - margin - tooltipWidth; }
    if (top < margin) { top = margin; }
    if (top + tooltipHeight > vh - margin) { top = vh - margin - tooltipHeight; }

    this.tooltipStyle = {
      top: top + 'px',
      left: left + 'px',
      transform: 'none'
    };
  }

  private getRole(): string {
    const user = this.userService.getCurrentUser();
    return user && user.role ? user.role : 'ROLE_API_MANAGER';
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.clearPositionTimer();
    this.disableScrollLock();
  }
}
